import React from 'react';

import Axios from 'axios';

import { isJobEvent } from '@libs/EventTypes';
import { getMoment, toISODate, isAllDay, showPeriodNoTime } from '@libs/NwMoment';
import { getEventFieldName } from '@libs/NwUtils';

import { getContactName } from '@hooks/API/Contact/UseContactsList';

import EventTypeTag from '@components/EventDetails/EventTypeTag';
import { EventLabelPanel, EventName } from '@components/Project/ProjectTransactions/Transactions/ProjectTransactionsUI';
import {
    getProjectModelsWithEventDates,
    isJobEventActive,
    isExtEventActive,
    isEventActive,
    sortProjectModelsByConfirmation,
} from '@components/Project/ProjectUtils';

export const TRANSACTION_FAMILIES = {
    Rate: 'JobFee',
    JobRefund: 'JobRefund',
    JobExpense: 'JobExpense',
    Product: 'Product',
};

export const MODEL_FIELD_CONDITIONS = {
    Required: 'required',
    Optional: 'optional',
    Hidden: 'hidden',
};

export const TRANSACTION_MODEL_OPTIONS = {
    [TRANSACTION_FAMILIES.JobFee]: MODEL_FIELD_CONDITIONS.Required,
    [TRANSACTION_FAMILIES.JobRefund]: MODEL_FIELD_CONDITIONS.Required,
    [TRANSACTION_FAMILIES.JobExpense]: MODEL_FIELD_CONDITIONS.Optional,
    [TRANSACTION_FAMILIES.Product]: MODEL_FIELD_CONDITIONS.Hidden,
};

export const getTransactionContacts = (currentProject, events) => {
    const callsheetContactIDs = currentProject.Callsheets.map((item) => item.ContactID);
    const clientIds = [
        currentProject.CustomerID,
        ...callsheetContactIDs.filter((id) => id !== currentProject.CustomerID),
    ];
    const contactsFromEvents = [];
    events.forEach((event) => {
        if (
            event.EventClientID &&
            !contactsFromEvents.find((c) => c.ID === event.EventClientID) &&
            !clientIds.find((id) => id === event.EventClientID)
        ) {
            contactsFromEvents.push({
                ID: event.EventClientID,
                Name: getContactName(event.EventClientID),
            });
        }
    });
    const contacts = clientIds.map((clientId) => {
        return {
            ID: clientId,
            Name: getContactName(clientId),
        };
    });
    return [...contacts, ...contactsFromEvents];
};

export const getTransactionContactBillingInfo = async (contactId) => {
    try {
        const billingInfo = await Axios.get(`/contacts/billinginfos/${contactId}`);
        //setClientBillingInfos(billingInfo.filter(item => !item.Disabled))
        return billingInfo.filter((item) => !item.Disabled);
    } catch (error) {
        console.error('error fetching billing info', error);
    }
};

export const getProjectModelsForJobFees = (currentProject, typeQuantity, groupWorkDays) => {
    const tempGroupedModelDates = [];

    const activeOnModel = groupWorkDays || typeQuantity === 'Units' || typeQuantity === 'Flat';

    for (const event of currentProject.Events) {
        for (const eventDate of event.EventDates) {
            for (const eventDateModel of eventDate.EventDatesModels) {
                const modelIndex = tempGroupedModelDates.findIndex(
                    (modelDate) => modelDate.ModelID === eventDateModel.ModelID
                );
                const eventDateDetails = {
                    EventID: event.ID,
                    EventDateID: eventDate.ID,
                    EventType: event.TypeName,
                    SubTypeCode: event.SubTypeCode,
                    OptionNumber: eventDateModel.OptionNumber,
                    Cancel: eventDateModel.Cancel,
                    EventName: event.Name,
                    Active: false,
                    Quantity: 1,
                    FromDate: eventDateModel.FromDate,
                    ToDate: eventDateModel.ToDate,
                    StartBreak: eventDateModel.StartBreak,
                    EndBreak: eventDateModel.EndBreak,
                };

                if (modelIndex > -1) {
                    tempGroupedModelDates[modelIndex].EventDates.push({
                        ...eventDateDetails,
                    });
                } else {
                    tempGroupedModelDates.push({
                        ModelID: eventDateModel.ModelID,
                        ModelName: getContactName(eventDateModel.ModelID),
                        Active: false,
                        Quantity: 0,
                        EventDates: [
                            {
                                ...eventDateDetails,
                            },
                        ],
                    });
                }
            }
        }
    }

    for (const model of tempGroupedModelDates) {
        const activeJobs = model.EventDates.filter((eventDate) => isJobEventActive(eventDate));

        //number of confirmed jobs to set the quantity for "group work days in a single fee" or 1 if typeQuantity is "Units" or "Flat"
        model.Quantity = activeOnModel
            ? typeQuantity === 'Units' || typeQuantity === 'Flat'
                ? 1
                : activeJobs.length
                  ? activeJobs.length
                  : 0
            : 0;

        model.AlwaysCancelled = model.EventDates.every((eventDate) => eventDate.Cancel);
        model.HasConfirmation = activeJobs.length > 0 ? true : false;
    }

    //how many models have confirmed jobs. Model will start active if there is only one model with confirmed jobs.
    const singleActiveModel = tempGroupedModelDates.filter((model) => model.HasConfirmation).length === 1;

    for (const model of tempGroupedModelDates) {
        //check for disabled switch
        model.Active = singleActiveModel && activeOnModel && model.HasConfirmation ? true : false;

        for (const eventDate of model.EventDates) {
            const isDateActive = singleActiveModel && isJobEventActive(eventDate);
            eventDate.Active = isDateActive;

            //calculate hours for TypeQuantity "Hours"
            const { ToDate, FromDate, StartBreak, EndBreak } = eventDate;
            let momentdiff = 8;
            if (StartBreak && EndBreak) {
                momentdiff = getMoment(ToDate)
                    .startOf('minute')
                    .diff(getMoment(EndBreak).startOf('minute'), 'hours', true);
                momentdiff += getMoment(StartBreak)
                    .startOf('minute')
                    .diff(getMoment(FromDate).startOf('minute'), 'hours', true);
            } else {
                if (isAllDay(FromDate, ToDate)) {
                    momentdiff = 8;
                } else {
                    momentdiff = getMoment(ToDate)
                        .startOf('minute')
                        .diff(getMoment(FromDate).startOf('minute'), 'hours', true);
                }
            }
            const qty = momentdiff;
            eventDate.Quantity = qty;
        }
    }

    return sortProjectModelsByConfirmation(tempGroupedModelDates);
};

export const getProjectModelsForCommAsMothAg = (currentProject, typeQuantity, groupWorkDays) => {
    const tempGroupedModelDates = [];

    const activeOnModel = groupWorkDays || typeQuantity === 'Units' || typeQuantity === 'Flat';

    for (const event of currentProject.Events) {
        for (const eventDate of event.EventDates) {
            for (const eventDateModel of eventDate.EventDatesModels) {
                const modelIndex = tempGroupedModelDates.findIndex(
                    (modelDate) => modelDate.ModelID === eventDateModel.ModelID
                );
                const eventDateDetails = {
                    EventID: event.ID,
                    EventDateID: eventDate.ID,
                    EventType: event.TypeName,
                    SubTypeCode: event.SubTypeCode,
                    OptionNumber: eventDateModel.OptionNumber,
                    Cancel: eventDateModel.Cancel,
                    EventName: event.Name,
                    Active: false,
                    Quantity: 1,
                    FromDate: eventDateModel.FromDate,
                    ToDate: eventDateModel.ToDate,
                    StartBreak: eventDateModel.StartBreak,
                    EndBreak: eventDateModel.EndBreak,
                };

                if (modelIndex > -1) {
                    tempGroupedModelDates[modelIndex].EventDates.push({
                        ...eventDateDetails,
                    });
                } else {
                    tempGroupedModelDates.push({
                        ModelID: eventDateModel.ModelID,
                        ModelName: getContactName(eventDateModel.ModelID),
                        Active: false,
                        Quantity: 0,
                        EventDates: [
                            {
                                ...eventDateDetails,
                            },
                        ],
                    });
                }
            }
        }
    }

    for (const model of tempGroupedModelDates) {
        const activeJobs = model.EventDates.filter((eventDate) => isExtEventActive(eventDate));

        //number of confirmed jobs to set the quantity for "group work days in a single fee" or 1 if typeQuantity is "Units" or "Flat"
        model.Quantity = activeOnModel
            ? typeQuantity === 'Units' || typeQuantity === 'Flat'
                ? 1
                : activeJobs.length
                  ? activeJobs.length
                  : 0
            : 0;

        model.AlwaysCancelled = model.EventDates.every((eventDate) => eventDate.Cancel);
        model.HasConfirmation = activeJobs.length > 0 ? true : false;
    }

    //how many models have confirmed jobs. Model will start active if there is only one model with confirmed jobs.
    const singleActiveModel = tempGroupedModelDates.filter((model) => model.HasConfirmation).length === 1;

    for (const model of tempGroupedModelDates) {
        //check for disabled switch
        model.Active = singleActiveModel && activeOnModel && model.HasConfirmation ? true : false;

        for (const eventDate of model.EventDates) {
            const isDateActive = singleActiveModel && isExtEventActive(eventDate);
            eventDate.Active = isDateActive;

            //calculate hours for TypeQuantity "Hours"
            const { ToDate, FromDate, StartBreak, EndBreak } = eventDate;
            let momentdiff = 8;
            if (StartBreak && EndBreak) {
                momentdiff = getMoment(ToDate)
                    .startOf('minute')
                    .diff(getMoment(EndBreak).startOf('minute'), 'hours', true);
                momentdiff += getMoment(StartBreak)
                    .startOf('minute')
                    .diff(getMoment(FromDate).startOf('minute'), 'hours', true);
            } else {
                if (isAllDay(FromDate, ToDate)) {
                    momentdiff = 8;
                } else {
                    momentdiff = getMoment(ToDate)
                        .startOf('minute')
                        .diff(getMoment(FromDate).startOf('minute'), 'hours', true);
                }
            }
            const qty = momentdiff;
            eventDate.Quantity = qty;
        }
    }

    return sortProjectModelsByConfirmation(tempGroupedModelDates);
};

export const getProjectModelsForTransactions = (currentProject) => {
    const tempGroupedModelDates = getProjectModelsWithEventDates(
        currentProject,
        getContactName,
        ({ event, eventDateModel }) => ({
            EventID: event.ID,
            EventType: event.TypeName,
            OptionNumber: eventDateModel.OptionNumber,
            Cancel: eventDateModel.Cancel,
            Active: false,
        })
    );

    for (const model of tempGroupedModelDates) {
        const activeJobs = model.EventDates.filter((eventDate) => isEventActive(eventDate));
        model.AlwaysCancelled = model.EventDates.length > 0 && model.EventDates.every((eventDate) => eventDate.Cancel);
        model.HasConfirmation = activeJobs.length > 0 ? true : false;
    }

    //how many models have confirmed jobs. Model will start active if there is only one model with confirmed jobs.
    const singleActiveModel = tempGroupedModelDates.filter((model) => model.HasConfirmation).length === 1;

    for (const model of tempGroupedModelDates) {
        //check for disabled switch
        model.Active = singleActiveModel && model.HasConfirmation ? true : false;

        for (const eventDate of model.EventDates) {
            const isDateActive = singleActiveModel && isEventActive(eventDate);
            eventDate.Active = isDateActive;
        }
    }

    return sortProjectModelsByConfirmation(tempGroupedModelDates);
};

export const getEventModelsForTransactions = (selectedEvent) => {
    const tempGroupedModels = [];
    if (selectedEvent) {
        for (const model of selectedEvent.Models) {
            tempGroupedModels.push({
                ModelID: model.ModelID,
                ModelName: getContactName(model.ModelID),
                Active: !model.Cancel,
                HasConfirmation: !model.Cancel,
                AlwaysCancelled: model.Cancel,
            });
        }
    }
    return tempGroupedModels;
};

export const getModelsOrDatesForNonBookingEventTransactions = (eventData, periodGroup) => {
    const eventOrDateModels = [
        {
            ModelID: eventData.ModelID,
            ModelName: periodGroup
                ? showPeriodNoTime(eventData.StartDate, eventData.EndDate)
                : getContactName(eventData.ModelID),
            Active: !eventData.Cancel && !periodGroup,
            AlwaysCancelled: eventData.Cancel,
            HasConfirmation: true,
            eventID: eventData.ID,
            periodFirstDate: getMoment(eventData.StartDate),
        },
    ];
    if (eventData.RelatedEvents) {
        for (const relEvent of eventData.RelatedEvents) {
            let addAsActive = !relEvent.Cancel && !periodGroup;
            eventOrDateModels.push({
                ModelID: relEvent.ModelID,
                ModelName: periodGroup
                    ? showPeriodNoTime(relEvent.StartDate, relEvent.EndDate)
                    : getContactName(relEvent.ModelID),
                Active: addAsActive,
                AlwaysCancelled: relEvent.Cancel,
                HasConfirmation: true,
                eventID: relEvent.ID,
                periodFirstDate: getMoment(relEvent.StartDate),
            });
        }
    }
    if (periodGroup) {
        eventOrDateModels.sort((a, b) => {
            // Push AlwaysCancelled items to the end
            if (a.AlwaysCancelled && !b.AlwaysCancelled) return 1;
            if (!a.AlwaysCancelled && b.AlwaysCancelled) return -1;
            // Both have same AlwaysCancelled status, sort by periodFirstDate
            return a.periodFirstDate.diff(b.periodFirstDate, 'days');
        });
        // set Active to true for the first instance if it's not AlwaysCancelled
        eventOrDateModels[0].Active = !eventOrDateModels[0].AlwaysCancelled;
    } else {
        // sort by model names, always cancelled at the end
        eventOrDateModels.sort((a, b) => {
            if (a.AlwaysCancelled && !b.AlwaysCancelled) return 1;
            if (!a.AlwaysCancelled && b.AlwaysCancelled) return -1;
            return a.ModelName.localeCompare(b.ModelName);
        });
    }
    return eventOrDateModels;
};

const getEventName = (event) => {
    switch (event.elType) {
        case 'Event':
            return event.Name;
        case 'FollowUp':
            return event.Description;
        case 'Travel':
            return `${event.StartLocation} - ${event.ArrivalLocation}`;
        default:
            return '';
    }
};

export const getEvents = (list) => {
    const events = list
        .filter((event) => {
            if (event.elType === 'Event' || event.elType === 'FollowUp' || event.elType === 'Travel') {
                return true;
            }
            return false;
        })
        .map((event) => {
            return {
                Label: (
                    <EventLabelPanel>
                        <EventTypeTag event={event} />
                        <EventName>{getEventName(event)}</EventName>
                    </EventLabelPanel>
                ),
                Event: event,
                Value: event.elType + event.ID,
            };
        });
    return events;
};

export const getInitialValues = (
    family,
    LABELS,
    event,
    project,
    modelId,
    modelOption,
    usageId,
    contactBillingInfo,
    modelBillingInfo,
    defaultCurrency,
    usage
) => {
    let ServiceID = event && event.ServiceID ? event.ServiceID : '';
    let Quantity = [1, 'Units']; // Quantity + TypeQuantity
    let ModelID = modelId ? modelId : undefined;
    let WorkDate = getMoment(Date.now());
    if (family === TRANSACTION_FAMILIES.JobFee) {
        Quantity = [Quantity[0], LABELS[0].TypeQuantityName];
    }
    if (family === TRANSACTION_FAMILIES.JobFee) {
        ServiceID = undefined;
    }
    if (modelOption === MODEL_FIELD_CONDITIONS.Hidden) {
        ModelID = undefined;
    }
    if (usage && usage.ModelID) {
        ModelID = usage.ModelID;
    }
    let defaultCost = [defaultCurrency, 0];
    if (usage && usage.PrevisionalTotalFee && usage.Currency) {
        defaultCost = [usage.Currency, usage.PrevisionalTotalFee];
    }

    const defaultCustomerBillingInfo = contactBillingInfo && contactBillingInfo.find((item) => item.isDefault);
    const defaultModelBillingInfo = modelBillingInfo && modelBillingInfo.find((item) => item.isDefault);

    //   if (family === TRANSACTION_FAMILIES.JobFee) {
    //     WorkDate = null;
    //   }
    return {
        Family: family,
        Label: LABELS[0].ID,
        Title: LABELS[0].Label,
        Description: '',
        WorkDate,
        ServiceID,
        ClientID: project && project.CustomerID,
        Cost: defaultCost, // Currency + SingleFee
        Quantity,
        AgencyCost: 0,
        AgencyCostActive: false,
        PaymentDate: getMoment(Date.now()),
        ModelID,
        EventID: event && event.elType !== 'Meeting' ? event.uniqID : null,
        UsageID: usageId || undefined,
        ClientBillingInfoID: defaultCustomerBillingInfo ? defaultCustomerBillingInfo.ID : undefined,
        ModelBillingInfoID: defaultModelBillingInfo ? defaultModelBillingInfo.ID : undefined,
    };
};

export const prepareTransactionData = (values, currentProject, event, allEvents) => {
    let selectedEvent = event;
    const data = {
        Family: values.Family,
        TransactionLabelID: values.Label,
        Title: values.Title,
        Description: values.Description,
        RefDate: values.WorkDate && values.WorkDate.format(),
        WorkDate: values.WorkDate && values.WorkDate.format(),
        Quantity: values.Quantity[0],
        TypeQuantity: values.Quantity[1],
        Currency: values.Cost[0],
        SingleFee: values.Cost[1],
        ServiceID: values.ServiceID,
        ProjectID: currentProject.ID,
        ClientID: values.ClientID,
        ModelID: values.ModelID,
        UsageID: values.UsageID,
        ClientBillingInfoID: values.ClientBillingInfoID,
        ServiceBillingInfoID: values.ServiceBillingInfoID,
        ModelBillingInfoID: values.ModelBillingInfoID,
    };

    if (event && event.GroupGuid) {
        const groupedEvents = allEvents.filter((e) => e.GroupGuid === event.GroupGuid);
        const eventByModelId = groupedEvents.find((e) => e.ModelID === values.ModelID);
        selectedEvent = eventByModelId ? eventByModelId : event;
    }

    if (selectedEvent) {
        const type = selectedEvent.elType || selectedEvent.Type;
        const eventFieldName = getEventFieldName(type.toLowerCase());
        data[eventFieldName] = selectedEvent.ID;
        return {
            ...data,
        };
    }

    return data;
};

export const isServiceToSet = (event) => {
    if (!event.ServiceID) {
        return false;
    }
    if (event.elType === 'Travel' || event.elType === 'FollowUp' || event.elType === 'Accommodation') {
        return true;
    }
    return false;
};

export const getCostLabel = (family) => {
    if (family === TRANSACTION_FAMILIES.Product) {
        return 'Amount';
    }
    if (family === TRANSACTION_FAMILIES.JobFee) {
        return 'Fee';
    }
    return 'Cost for the client';
};

export const checkDate = (values, events, rangeDate, family) => {
    const { EventID: eventID, ModelID: modelId, Quantity: quantity } = values;
    if (
        !eventID ||
        !modelId ||
        family !== TRANSACTION_FAMILIES.JobFee ||
        (quantity[1] !== 'Days' && quantity[1] !== 'Hours')
    ) {
        return false;
    }
    if (quantity[1] !== 'Days' && quantity[1] !== 'Hours') {
        return false;
    }
    const event = events.find((event) => event.ID === parseInt(eventID.split('-')[1]));
    if (event) {
        const period = event.EventDates.find((eventDate) => {
            if (!eventDate.EventDatesModels) {
                return false;
            }
            const model = eventDate.EventDatesModels.find((m) => m.ModelID === modelId);
            if (
                toISODate(rangeDate) >= toISODate(eventDate.FromDate) &&
                toISODate(rangeDate) <= toISODate(eventDate.ToDate) &&
                model
            ) {
                return true;
            }
            return false;
        });
        if (period) {
            return false;
        }
    }
    return true;
};

export const groupTransactionLabels = (LABELS) => {
    let groupedLabels = [{ groupName: '', options: [] }];

    for (const transactionLabel of LABELS) {
        const groupIndex = groupedLabels.findIndex((group) => group.groupName === transactionLabel.Group);
        if (groupIndex > -1) {
            groupedLabels[groupIndex].options.push({
                value: transactionLabel.ID,
                label: transactionLabel.Label,
            });
        } else {
            if (!transactionLabel.Group) {
                groupedLabels[0].options.push({
                    value: transactionLabel.ID,
                    label: transactionLabel.Label,
                });
            } else {
                groupedLabels.push({
                    groupName: transactionLabel.Group,
                    options: [
                        {
                            value: transactionLabel.ID,
                            label: transactionLabel.Label,
                        },
                    ],
                });
            }
        }
    }

    return groupedLabels;
};

// Restituisce l'unico evento di booking della lista che supera il filtro tipo
// (default: job/ext), se quello e' anche confermato. Pensato per pre-popolare
// il selettore evento nei form transazione quando l'evento di riferimento e'
// inequivocabile. Passa `typeFilter` per restringere il dominio (es. solo job
// per JobFee, solo ext per JobCommAsMothAg).
export const getSingleConfirmedJobEvent = (events, typeFilter = isJobEvent) => {
    const jobEvents = events.filter(
        (event) => event.elType === 'Event' && event.TypeName && typeFilter(event.TypeName)
    );
    if (jobEvents.length === 1 && jobEvents[0].Confirmed > 0) {
        return jobEvents[0];
    }
    return null;
};

// Restituisce la FromDate del primo EventDate dell'evento che contiene almeno
// un modello con OptionNumber === 10 e non cancellato. Se non c'e' alcuna data
// confermata ricade sul primo EventDate, e infine su event.FirstDate.
export const getEventFirstConfirmedDate = (event) => {
    if (!event) return null;
    if (event.EventDates && event.EventDates.length > 0) {
        const confirmedDate = event.EventDates.find((eventDate) =>
            (eventDate.EventDatesModels || []).some((m) => m.OptionNumber === 10 && !m.Cancel)
        );
        if (confirmedDate) return confirmedDate.FromDate;
        return event.EventDates[0].FromDate;
    }
    return event.FirstDate;
};

export const handleChangeEventForDate = (event, setFieldValue) => {
    switch (event.elType) {
        case 'Accommodation':
            setFieldValue('RefDate', getMoment(event.FromDate));
            break;
        case 'Travel':
            setFieldValue('RefDate', getMoment(event.StartDateTime));
            break;
        case 'FollowUp':
            setFieldValue('RefDate', getMoment(event.StartDate));
            break;
        case 'Meeting':
            setFieldValue('RefDate', getMoment(event.StartDate));
            break;
        case 'Event':
            setFieldValue('RefDate', getMoment(event.FirstDate));
            break;
        default:
            break;
    }
};
